import { ArrowRight } from 'lucide-react'
import { type KeyboardEvent } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { InquiryForm } from '../components/InquiryForm'
import { PageHero } from '../components/PageHero'
import { images } from '../data/images'
import { serviceAvailabilityNote, serviceMenus, type ServiceCategory } from '../data/services'
import { contact } from '../data/site'

type ServiceTab = 'salon' | 'spa'

function ServiceCategorySection({ category }: { category: ServiceCategory }) {
  return (
    <section className="price-section reveal">
      <div className="price-section__heading"><span>{category.number}</span><h3>{category.title}</h3></div>
      <p className="price-section__description">{category.description}</p>
      <ul className="price-section__services">
        {category.items.map((item) => (
          <li key={item.name}>
            <span>{item.name}</span>
            <i aria-hidden="true" />
            <strong data-price-type={item.priceType}>{item.price}</strong>
          </li>
        ))}
      </ul>
      {category.options && (
        <div className="price-section__options">
          <h4>{category.optionsLabel}</h4>
          <ul>{category.options.map((option) => <li key={option}>{option}</li>)}</ul>
        </div>
      )}
    </section>
  )
}

export function Services() {
  const [searchParams, setSearchParams] = useSearchParams()
  const tab: ServiceTab = searchParams.get('view') === 'spa' ? 'spa' : 'salon'
  const content = serviceMenus[tab]

  const selectTab = (next: ServiceTab, focus = false) => {
    if (next !== tab) setSearchParams({ view: next }, { replace: true })
    if (focus) requestAnimationFrame(() => document.getElementById(`${next}-tab`)?.focus({ preventScroll: true }))
  }

  const handleTabKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    let next: ServiceTab | undefined
    if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') next = tab === 'salon' ? 'spa' : 'salon'
    if (event.key === 'Home') next = 'salon'
    if (event.key === 'End') next = 'spa'
    if (!next) return
    event.preventDefault()
    selectTab(next, true)
  }

  return (
    <>
      <PageHero
        className="page-hero--services"
        eyebrow="Service Menu"
        title="Services"
        image={tab === 'salon' ? images['services-salon'] : images['services-spa']}
        objectPosition={tab === 'salon' ? 'center 55%' : 'center 56%'}
      />
      <section className="section shell services-section">
        <div className="services-content">
          <div className="service-tabs reveal" role="tablist" aria-label="Choose service menu">
            <button id="salon-tab" className={tab === 'salon' ? 'is-active' : ''} type="button" onClick={() => selectTab('salon')} onKeyDown={handleTabKeyDown} role="tab" aria-selected={tab === 'salon'} aria-controls="service-menu" tabIndex={tab === 'salon' ? 0 : -1}>Salon Services</button>
            <button id="spa-tab" className={tab === 'spa' ? 'is-active' : ''} type="button" onClick={() => selectTab('spa')} onKeyDown={handleTabKeyDown} role="tab" aria-selected={tab === 'spa'} aria-controls="service-menu" tabIndex={tab === 'spa' ? 0 : -1}>Spa Services</button>
          </div>

          <div id="service-menu" className="service-menu" role="tabpanel" aria-labelledby={`${tab}-tab`}>
            <div className="services-intro reveal reveal--heading">
              <div>
                <span className="eyebrow">{content.eyebrow}</span>
                <h2>{content.title}</h2>
              </div>
              <p>{content.introduction}</p>
            </div>

            <div className="price-list">
              {content.categories.map((category) => <ServiceCategorySection category={category} key={category.title} />)}
            </div>

            {content.pricingNote && <p className="service-pricing-note reveal">{content.pricingNote}</p>}

            <aside className="service-booking reveal reveal--heading">
              <div>
                <span className="eyebrow">Plan Your Visit</span>
                <h2>Ready to plan your appointment?</h2>
                <p>{serviceAvailabilityNote}</p>
              </div>
              <div className="service-booking__actions">
                <a className="button" href={contact.bookingUrl} target="_blank" rel="noopener noreferrer">Book an Appointment</a>
                <Link className="text-link" to="/contact">Contact the Salon <ArrowRight /></Link>
              </div>
            </aside>
          </div>
        </div>
      </section>
      <InquiryForm />
    </>
  )
}
